import './index.less';

import { Button, Flex, Icon, Text } from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import { useMenusItemStyle } from '../CasHeader/AccountSetting/style';
import { FormInput } from '../Common/FormComponent';
import IconButton from '../IconButton';
import useSenderListStyle from './style';
import { InlineError } from '../FilterNotification/NotifyAdmin';
import { ValidProps } from '../../types';
import SmartTooltip from '@Report/SmartTooltip';

export const FlexOption = ({
  style,
  action,
  ...props
}: {
  action?: {
    delete?: {
      icon?: { name: string; component: JSX.Element };
      handler: Function;
      disabled?: boolean;
    };
  };
  [key: string]: any;
}) => {
  // console.log(style, action);
  const menusItemStyle = useMenusItemStyle();
  useWhyDidYouUpdate('EditableList-FlexOption', { style, action, ...props });
  if (action && action.delete) {
    return (
      <Flex
        alignItems="center"
        height="8x"
        minHeight="8x"
        px="3x"
        justifyContent="space-between"
        {...menusItemStyle}
        {...props}
        {...style}
      >
        <Flex>{props.children}</Flex>
        <Flex>
          <IconButton
            disabled={action?.delete?.disabled || false}
            onClick={() => {
              action?.delete?.handler(props.value);
            }}
          >
            {action.delete.icon ? (
              action.delete.icon.component
            ) : (
              <Icon as={DeleteIcon} />
            )}
          </IconButton>
        </Flex>
      </Flex>
    );
  } else {
    return (
      <Flex
        alignItems="center"
        height="8x"
        minHeight="8x"
        px="3x"
        justifyContent="space-between"
        {...menusItemStyle}
        {...props}
      >
        <Flex>{props.children}</Flex>
      </Flex>
    );
  }
};
const FlexList = ({ style, ...props }: { [key: string]: any }) => {
  // console.log(style);
  useWhyDidYouUpdate('EditableList-FlexList', { style, ...props });
  const styles = useSenderListStyle();
  const { flexListStyle } = styles;
  const newStyle = { ...flexListStyle, ...style };
  return <Flex style={newStyle} {...props} />;
};
// limit as -1, means no limit
function EditableList(props: {
  list: { name: string; value: string }[];
  onDelete: Function;
  onAdd: Function;
  disabled: boolean;
  limit: number;
  description: string;
  size: number;
  width: string;
  placeholder?: string;
  onImport?: Function;
  onExport?: Function;
  validates?: ValidProps[];
  [key: string]: any;
}) {
  const { t } = useTranslation();
  const { descriptionStyle } = useSenderListStyle();
  const [inputValue, setInputValue] = React.useState('');
  const prefixRef = React.useRef(Math.random().toString(36).slice(4));
  const {
    list,
    onDelete,
    onAdd,
    onImport,
    onExport,
    disabled,
    limit,
    description,
    width,
    placeholder,
    size,
    validates,
  } = props;
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>(undefined);
  const inputValueRef = useRef<string | undefined>(undefined);
  const validate = (value: string) => {
    let isValid = true;
    validates?.forEach((valid) => {
      if (!valid.validMethod(value)) {
        isValid = false;
        setErrorMsg(valid.errMsg);
        inputValueRef.current = valid.errMsg;
      }
    });
    if (isValid) {
      setErrorMsg(undefined);
      inputValueRef.current = undefined;
    }
  };
  const height = React.useMemo(() => {
    const baseHeight = 86;
    const height = baseHeight + size * 32;
    return `${height}px`;
  }, [size]);
  const action = React.useMemo(() => {
    return {
      delete: {
        handler: onDelete,
        disabled,
      },
    };
  }, [onDelete, disabled]);
  useWhyDidYouUpdate('EditableList', props);
  return (
    <div>
      <FlexList width={width} direction="column" height={height}>
        <Flex alignItems="center">
          <FormInput
            placeholder={
              placeholder || t('policy.approvedAndBlockedList.emailOrDomain')
            }
            width="75%"
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setInputValue(e.target.value);
            }}
            error={errorMsg !== undefined}
            disabled={disabled}
            maxLength={255}
          />
          <Button
            ml="3x"
            onClick={() => {
              if (validates) {
                validate(inputValue);
              }
              if (inputValueRef.current !== undefined) {
                return;
              }
              onAdd(inputValue);
              setInputValue('');
            }}
            disabled={
              disabled ||
              inputValue === '' ||
              (limit === -1 ? false : list.length >= limit)
            }
          >
            {t('common.action.label.add')}
          </Button>
          {onImport && (
            <Button
              ml="3x"
              variant="secondary"
              onClick={(e) => onImport(e)}
              disabled={disabled}
            >
              {t('common.action.label.import')}
            </Button>
          )}
          {onExport && (
            <Button
              ml="3x"
              variant="secondary"
              onClick={() => onExport(list)}
              disabled={list.length === 0}
            >
              {t('common.action.label.export')}
            </Button>
          )}
        </Flex>
        <Flex>
          {description && (
            <Text style={descriptionStyle} width="600px">
              {description}
            </Text>
          )}
        </Flex>
        <Flex>
          {errorMsg !== undefined && <InlineError>{errorMsg}</InlineError>}
        </Flex>
        <Flex direction="column">
          {list.map((item) => {
            return (
              <FlexOption
                value={item.value}
                action={action}
                key={`${prefixRef.current}-${item.value}`}
              >
                <Text maxWidth={600}>
                  <SmartTooltip>{item.name}</SmartTooltip>
                </Text>
              </FlexOption>
            );
          })}
        </Flex>
      </FlexList>
    </div>
  );
}

export default React.memo(EditableList);
